/**
 * 标准配合求解器世界包围盒工具。
 *
 * 文件职责：
 * 1. 将运行时传入的 world AABB 保持为求解器 plain data；
 * 2. 在组件 transform 被求解器更新后，用同一个 world delta 同步更新 bounds；
 * 3. 为临时 Mate 初始姿态提供沿指定轴向的投影区间。
 */

import * as THREE from 'three';
import type { AssemblyMateSolverWorldBounds } from '../StandardMateSolverTypes';

/**
 * 包围盒在指定轴向上的投影区间。
 */
export interface StandardMateWorldBoundsAxisInterval {
  /** 区间最小投影。 */
  readonly min: number;
  /** 区间最大投影。 */
  readonly max: number;
  /** 区间中心投影。 */
  readonly center: number;
  /** 区间长度。 */
  readonly length: number;
}

/**
 * 将包围盒三元组转换为 Vector3。
 *
 * @param value 包围盒顶点三元组。
 * @returns Vector3。
 */
function vectorFromBoundsTuple(value: readonly [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(value[0], value[1], value[2]);
}

/**
 * 将 Vector3 转换为包围盒三元组。
 *
 * @param value Vector3。
 * @returns 包围盒顶点三元组。
 */
function boundsTupleFromVector(value: THREE.Vector3): readonly [number, number, number] {
  return [value.x, value.y, value.z] as const;
}

/**
 * 收集世界 AABB 的八个角点。
 *
 * @param bounds 世界包围盒。
 * @returns 包围盒角点。
 */
function collectWorldBoundsCorners(bounds: AssemblyMateSolverWorldBounds): THREE.Vector3[] {
  const min = vectorFromBoundsTuple(bounds.min);
  const max = vectorFromBoundsTuple(bounds.max);
  return [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ];
}

/**
 * 应用世界 delta 后重新计算移动件世界 AABB。
 *
 * @param bounds 原始世界包围盒。
 * @param delta 世界空间变换。
 * @returns 变换后的世界包围盒。
 */
export function transformStandardMateWorldBounds(
  bounds: AssemblyMateSolverWorldBounds,
  delta: THREE.Matrix4,
): AssemblyMateSolverWorldBounds {
  const box = new THREE.Box3();
  collectWorldBoundsCorners(bounds).forEach((corner) => {
    box.expandByPoint(corner.applyMatrix4(delta));
  });

  return {
    min: boundsTupleFromVector(box.min),
    max: boundsTupleFromVector(box.max),
  };
}

/**
 * 计算包围盒沿指定轴向的投影区间。
 *
 * @param bounds 世界包围盒。
 * @param axisDirection 轴向单位向量。
 * @returns 投影区间。
 */
export function projectStandardMateWorldBoundsToAxisInterval(
  bounds: AssemblyMateSolverWorldBounds,
  axisDirection: THREE.Vector3,
): StandardMateWorldBoundsAxisInterval {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  collectWorldBoundsCorners(bounds).forEach((corner) => {
    const projection = corner.dot(axisDirection);
    min = Math.min(min, projection);
    max = Math.max(max, projection);
  });

  return {
    min,
    max,
    center: (min + max) / 2,
    length: max - min,
  };
}
